import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, ChevronDown, MessageSquare, Lock, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const inr = n => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const when = v => (v ? new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const Remarks = ({ list, sid }) => (
  <div className="space-y-1.5" data-testid={`overdue-remarks-${sid}`}>
    {list.length === 0 ? <p className="text-[11px] text-slate-400 italic">No remarks from management or counsellors yet.</p> : list.map((r, i) => (
      <div key={i} className="text-xs border-l-2 border-violet-200 pl-2">
        <div className="text-slate-700 whitespace-pre-wrap">{r.text}</div>
        <div className="text-[10px] text-slate-400">{r.source} · {r.by} · {when(r.at)}</div>
      </div>
    ))}
  </div>
);

const StudentRow = ({ s }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`overdue-student-${s.student_id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full grid grid-cols-12 gap-2 items-center px-4 py-2.5 text-left hover:bg-slate-50 transition-colors" data-testid={`overdue-student-toggle-${s.student_id}`}>
        <div className="col-span-12 sm:col-span-4">
          <div className="text-sm font-medium text-slate-900">{s.name}</div>
          <div className="text-[11px] text-slate-400 font-mono">{s.enrollment_no} · {s.phone}</div>
        </div>
        <div className="col-span-6 sm:col-span-2">
          <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200" data-testid={`overdue-amount-${s.student_id}`}>Overdue{s.overdue_amount > 0 ? ` ${inr(s.overdue_amount)}` : ' (CRM)'}</Badge>
        </div>
        <div className="col-span-6 sm:col-span-3 flex flex-wrap gap-1">
          {s.on_hold && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200"><Lock className="w-3 h-3 mr-1" />Batch {s.crm_batch_status}</Badge>}
          {s.crm_payment_status && <Badge variant="outline" className="text-[10px] text-slate-600">CRM: {s.crm_payment_status}</Badge>}
        </div>
        <div className="col-span-10 sm:col-span-2 text-[11px] text-slate-500 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{s.remarks.length} remark{s.remarks.length === 1 ? '' : 's'}</div>
        <ChevronDown className={`col-span-2 sm:col-span-1 w-4 h-4 text-slate-400 justify-self-end transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 grid md:grid-cols-2 gap-4 bg-slate-50/50">
          <div className="text-xs space-y-1 pt-2">
            <div className="font-semibold text-slate-700">Payment</div>
            <div className="text-slate-600">Fee {inr(s.total_amount)} · Paid {inr(s.paid_amount)} · Balance {inr(s.due_amount)}</div>
            {s.overdue_invoices.map(i => (
              <div key={i.invoice_number} className="text-slate-500">{i.invoice_number}: {inr(i.balance)} due {i.due_date}{i.days_overdue > 0 ? ` (${i.days_overdue} days late)` : ''}</div>
            ))}
            {s.counsellor && <div className="text-slate-500">Counsellor: {s.counsellor}</div>}
          </div>
          <div className="pt-2"><div className="text-xs font-semibold text-slate-700 mb-1">Remarks</div><Remarks list={s.remarks} sid={s.student_id} /></div>
        </div>
      )}
    </div>
  );
};

const BatchCard = ({ g }) => (
  <Card className="overflow-hidden" data-testid={`overdue-batch-${g.batch_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100">
      <div>
        <div className="text-sm font-bold text-slate-900">{g.name} <span className="text-[11px] font-normal text-slate-400">#{g.batch_number}</span></div>
        <div className="text-[11px] text-slate-500">{g.course_name} · {g.batch_status} · {g.start_date}{g.end_date ? ` → ${g.end_date}` : ''}</div>
      </div>
      <div className="text-right">
        <div className="text-sm font-bold text-rose-600">{g.overdue_total > 0 ? inr(g.overdue_total) : 'Overdue (CRM)'}</div>
        <div className="text-[11px] text-slate-500">{g.students.length} of {g.student_count} students overdue</div>
      </div>
    </div>
    {g.students.map(s => <StudentRow key={s.student_id} s={s} />)}
  </Card>
);

export default function TrainerOverduePage() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('active');
  const [q, setQ] = useState('');
  useEffect(() => { api.get('/trainer/overdue').then(r => setD(r.data)).catch(() => { toast.error('Could not load overdue students'); setD({ active: [], completed: [], summary: {} }); }); }, []);
  if (!d) return <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500 inline" /></div>;
  const term = q.trim().toLowerCase();
  const groups = d[tab].map(g => ({ ...g, students: g.students.filter(s => !term || `${s.name} ${s.enrollment_no} ${g.name}`.toLowerCase().includes(term)) })).filter(g => g.students.length);
  const sm = d.summary[tab] || {};
  return (
    <div className="space-y-5" data-testid="trainer-overdue-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Overdue students</h1>
        <p className="text-sm text-slate-500">Students in your batches with fees overdue, with the reasons noted by management and counsellors. View only.</p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="active" data-testid="overdue-tab-active">Running & upcoming · {d.summary.active?.students || 0} students</TabsTrigger>
            <TabsTrigger value="completed" data-testid="overdue-tab-completed">Completed batches · {d.summary.completed?.students || 0} students</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search student or batch" className="h-8 pl-8 text-xs" data-testid="overdue-search" />
        </div>
      </div>
      <p className="text-xs text-slate-600" data-testid="overdue-summary">{sm.students || 0} students across {sm.batches || 0} batches · <b className="text-rose-600">{inr(sm.amount)}</b> overdue</p>
      {groups.length === 0 ? <Card className="p-10 text-center text-sm text-slate-400" data-testid="overdue-empty">No overdue students here.</Card>
        : <div className="space-y-4">{groups.map(g => <BatchCard key={g.batch_id} g={g} />)}</div>}
    </div>
  );
}
